import { useEffect, useState } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { shallow } from '@jwp/ott-common/src/utils/compare';

import useContentProtection from './useContentProtection';
import { useAppConfig } from './useAppConfig';

export default function useProtectedMedia(item: PlaylistItem) {
  const apiService = getModule(ApiService);
  const { config } = useConfigStore(({ config }) => ({ config }), shallow);
  const ids = config.menu.map((m) => m?.custom?.drmMediaId || m?.custom?.drmPlaylistId).filter((e) => e !== undefined) as string[];
  const { custom } = useConfigStore(({ config }) => ({
    configId: config.id,
    signingConfig: config.contentSigningService,
    contentProtection: config.contentProtection,
    jwp: config.integrations.jwp,
    custom: config.custom || {},
  }));

  const { adConfigLevel, isAdEnabled, featuredAdDeliveryMethod } = useAppConfig();

  const isDRMContent = ids.includes(item.mediaid) || ids.includes(item.feedid);

  const [isGeoBlocked, setIsGeoBlocked] = useState(false);
  function getAdConfigId() {
    switch (adConfigLevel) {
      case 'global':
        return custom.drmAdConfigId;
      case 'media':
        return item.adConfig;
      case 'globalandmedia':
        return custom.drmAdConfigId;
    }
  }

  const contentProtectionQuery = useContentProtection(
    'media',
    item.mediaid,
    (token, drmPolicyId) => {
      if (drmPolicyId) {
        // For DRM content
        return apiService.getDrmMediaById(
          item.mediaid,
          custom.drmSiteId as string,
          token!,
          drmPolicyId,
          getAdConfigId() as string,
          featuredAdDeliveryMethod,
          isAdEnabled,
        );
      } else {
        // For normal (non-DRM) content
        return apiService.getMediaById(item.mediaid, token);
      }
    },
    {},
    true,
    null,
    isDRMContent,
    featuredAdDeliveryMethod,
    custom.drmSiteId,
    getAdConfigId,
  );

  useEffect(() => {
    const m3u8 = contentProtectionQuery.data?.sources.find((source) => source.file.indexOf('.m3u8') !== -1);
    if (m3u8) {
      fetch(m3u8.file, { method: 'GET' }).then((response) => {
        response.status === 403 && setIsGeoBlocked(true);
      });
    }
  }, [contentProtectionQuery.data]);

  return {
    ...contentProtectionQuery,
    isGeoBlocked,
  };
}
